import React from "react";
import PropTypes from "prop-types"; // PropTypes for type checking
import { connect } from "react-redux"; // Redux connect for state management
import moment from "moment"; // Moment.js for date and time formatting
import { makeStyles, useTheme } from "@material-ui/core/styles"; // Material-UI styling utilities
import TableSortLabel from "@material-ui/core/TableSortLabel"; // Table sorting label
import MenuItem from "@material-ui/core/MenuItem"; // Menu item for dropdowns
import Grid from "@material-ui/core/Grid"; // Grid layout
import ClickAwayListener from "@material-ui/core/ClickAwayListener"; // ClickAwayListener for closing popups
import Table from "@material-ui/core/Table"; // Table component
import TableHead from "@material-ui/core/TableHead"; // Table header
import isEmpty from "lodash/isEmpty"; // Lodash utility for checking empty objects
import TableBody from "@material-ui/core/TableBody"; // Table body
import TableCell from "@material-ui/core/TableCell"; // Table cell
import TableContainer from "@material-ui/core/TableContainer"; // Table container
import TableFooter from "@material-ui/core/TableFooter"; // Table footer
import useMediaQuery from "@material-ui/core/useMediaQuery"; // Media query hook for responsive design
import TablePagination from "@material-ui/core/TablePagination"; // Table pagination
import TableRow from "@material-ui/core/TableRow"; // Table row
import Paper from "@material-ui/core/Paper"; // Paper component for elevation
import IconButton from "@material-ui/core/IconButton"; // Icon button
import FirstPageIcon from "@material-ui/icons/FirstPage"; // First page icon for pagination
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft"; // Left arrow icon for pagination
import { Typography } from "@material-ui/core"; // Typography for text styling
import Popper from "@material-ui/core/Popper"; // Popper for dropdowns
import Grow from "@material-ui/core/Grow"; // Grow animation
import MenuList from "@material-ui/core/MenuList"; // Menu list for dropdowns
import { exportToPDF } from "modules/reports/utils/common"; // Utility for exporting to PDF
import { withStyles } from "@material-ui/styles"; // Material-UI withStyles for custom styling
import Tooltip from "@material-ui/core/Tooltip"; // Tooltip component
import { exportToCSVProvider } from "../../Utils/commonUtils"; // Utility for exporting to CSV
import noDataImage from "../../assets/img/noDataImage.png"; // No data image
import ErrorSearchDialog from "../../components/DialogBox/ErrorSearchDialog"; // Custom search dialog
import tableStyle from "../../assets/jss/material-dashboard-react/components/reportTables"; // Custom table styles
import { actionPermission, permissionCheck } from "../../Utils/commonUtils"; // Permission utilities
import { ReactComponent as ExportIcon } from "../../assets/svgIcons/ExportIcon.svg"; // Export icon
import {
  sendInviteApiCall,
  sendInviteManagerApiCall,
} from "../../actions/Operations"; // Redux actions for operations
import {
  getErrorCodeData,
  emptyReduxState,
  setFormData,
  clearFormData,
  getErrorCodeExport,
} from "../../Redux/Actions/merchantInformation"; // Redux actions for error codes
import SearchFields from "../../components/SearchComponent/SearchFields"; // Search fields component
import SearchChips from "../../components/SearchComponent/SearchChips"; // Search chips component
import { profileCodePermissionCheck } from "../../Utils/commonUtils"; // Utility for profile code checks

// Custom tooltip styles
const IconsTooltip = withStyles(() => ({
  tooltip: {
    backgroundColor: "#000", // Black background for tooltips
    fontSize: "12px", // Font size for tooltips
  },
  tooltipPlacementTop: {
    margin: "4px 0", // Margin for tooltips placed at the top
  },
}))(Tooltip);

// Custom table styles
const useStyles = makeStyles(tableStyle);

// Function to create table data
function createData(ErrorCode, type, Description, Entered_On) {
  return {
    ErrorCode, // Error code
    type, // Type of error
    Description, // Description of the error
    Entered_On, // Date when the error was entered
  };
}

// Rows for the table
let rows = [];

// Pagination actions for the table
function TablePaginationActions(props) {
  const classes = useStyles();
  const theme = useTheme();

  const {
    count, page, rowsPerPage, onPageChange,
  } = props;

  const handleFirstPageButtonClick = event => {
    onPageChange(event, 0); // Navigate to the first page
  };

  const handleBackButtonClick = event => {
    onPageChange(event, page - 1); // Navigate to the previous page
  };

  const handleNextButtonClick = event => {
    onPageChange(event, page + 1); // Navigate to the next page
  };

  const handleLastPageButtonClick = event => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1)); // Navigate to the last page
  };

  return (
    <div className={classes.rootPagination}>
      {/* First page button */}
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        variant="body2"
        aria-label="first page"
      >
        {theme.direction === "rtl" ? (
          <Typography>Last</Typography>
        ) : (
          <Typography variant="body2">First</Typography>
        )}
      </IconButton>
      {/* Previous page button */}
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <Typography>Previous</Typography>
        ) : (
          <Typography variant="body2">Previous</Typography>
        )}
      </IconButton>
      {/* Current page indicator */}
      <IconButton
        onClick={handleFirstPageButtonClick}
        aria-label="first page"
        className={classes.bottonPage}
      >
        <Typography variant="body2">{page ? page + 1 : 1}</Typography>
      </IconButton>
      {/* Next page button */}
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <Typography variant="body2">Next</Typography>
        )}
      </IconButton>
      {/* Last page button */}
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? (
          <FirstPageIcon />
        ) : (
          <Typography variant="body2">Last</Typography>
        )}
      </IconButton>
    </div>
  );
}

// Prop types for pagination actions
TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired, // Total number of rows
  onPageChange: PropTypes.func.isRequired, // Function to handle page change
  page: PropTypes.number.isRequired, // Current page number
  rowsPerPage: PropTypes.number.isRequired, // Number of rows per page
};

// Enhanced table header for sorting
function EnhancedTableHead(props) {
  const {
    classes, order, orderBy, onRequestSort,
  } = props;

  const createSortHandler = property => event => {
    onRequestSort(event, property); // Handle sorting
  };

  const headCells = [
    {
      id: "ErrorCode",
      numeric: false,
      disablePadding: true,
      label: "Error Code", // Column for error codes
      headClassName: classes.DBA_Name,
    },
    {
      id: "type",
      numeric: false,
      disablePadding: false,
      label: "Type", // Column for error types
      headClassName: classes.Assign_To,
    },
    {
      id: "Description",
      numeric: true,
      disablePadding: false,
      label: "Description", // Column for error descriptions
      headClassName: classes.email,
    },
    {
      id: "Entered_On",
      numeric: true,
      disablePadding: false,
      label: "Updated On", // Column for the date the error was updated
      headClassName: classes.Phone,
    },
  ];

  return (
    <TableHead className={classes.tableHeader}>
      <TableRow>
        {headCells.map(headCell => (
          <TableCell
            key={headCell.id}
            className={classes.cellTextHeaderOffer}
            align="left"
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

// Prop types for enhanced table header
EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired, // Custom styles
  onRequestSort: PropTypes.func.isRequired, // Function to handle sorting
  order: PropTypes.oneOf(["asc", "desc"]).isRequired, // Sorting order
  orderBy: PropTypes.string.isRequired, // Column to sort by
};

// Main component for Error Codes
const ErrorCodes = props => {
  const classes = useStyles();
  const [page, setPage] = React.useState(
    props.errorCodeData?.errorCodeData?.page
  );
  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("Entered_On");
  const [selected, setSelected] = React.useState([]);
  const [chipValue, setChipValue] = React.useState([]);
  const theme = useTheme();
  const windowWidth = useMediaQuery(theme.breakpoints.up("md"));
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [openImportFileModal, setImportFileModalOpen] = React.useState(false);
  const [searchBar, setSearchBar] = React.useState(false);
  const anchorRef = React.useRef(null);
  const [id, setId] = React.useState(null);
  const profileId = localStorage.getItem("profileId");
  const menu = JSON.parse(localStorage.getItem("menu"));
  const handleToggle = () => {
    setImportFileModalOpen(prevOpen => !prevOpen);
  };

  const handleCloseModel = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setImportFileModalOpen(false);
  };

  function handleListKeyDown(event) {
    if (event.key === "Tab") {
      event.preventDefault();
      setImportFileModalOpen(false);
    }
  }

  React.useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const idFromUrl = queryParams.get("id");
    setId(idFromUrl);
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setPage(1);
    if (id) {
      const payload = {
        Profile_Code: menu?.Role_Permission_Data?.Profile_Code,
        recordPerPage: rowsPerPage,
        pageNo: 1,
        sortColumn: orderBy,
        sortOrder: order,
        searchErrorCode: props?.errorCodeData?.searchErrorCode || "",
        searchDescription: props?.errorCodeData?.searchDescription || "",
        searchDate: props?.errorCodeData?.searchDate || "",
        searchType: props?.errorCodeData?.searchType || "",
      };

      props.getErrorCodeData(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  React.useEffect(() => {
    setPage(1);
    const payload = {
      Profile_Code: menu?.Role_Permission_Data?.Profile_Code,
      recordPerPage: rowsPerPage,
      pageNo: 1,
      sortColumn: orderBy,
      sortOrder: order,
      searchErrorCode: props?.errorCodeData?.searchErrorCode || "",
      searchDescription: props?.errorCodeData?.searchDescription || "",
      searchDate: props?.errorCodeData?.searchDate || "",
      searchType: props?.errorCodeData?.searchType || "",
    };
    props.getErrorCodeData(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React.useEffect(() => {
  //   if (props?.errorCodeData?.errorCodeData?.page !== page) {
  //     setPage(props?.errorCodeData?.errorCodeData?.page);
  //   }
  //   if (props?.errorCodeData?.errorCodeData?.limit !== rowsPerPage) {
  //     setRowsPerPage(props?.errorCodeData?.errorCodeData?.limit);
  //   }
  // }, [props]);

  React.useEffect(() => {
    const updatedFormData = {
      sortColumn: orderBy,
      sortOrder: order,
      recordPerPage: rowsPerPage,
      pageNo: page,
      searchErrorCode: "",
      searchDescription: "",
      searchDate: "",
      searchType: null,
    };
    props.setFormData(updatedFormData);
    props.clearFormData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    const requestData = {
      Profile_Code: menu?.Role_Permission_Data?.Profile_Code,
      recordPerPage: rowsPerPage,
      pageNo: newPage + 1,
      sortColumn: orderBy,
      sortOrder: order,
      searchErrorCode: props?.errorCodeData?.searchErrorCode || "",
      searchDescription: props?.errorCodeData?.searchDescription || "",
      searchDate: props?.errorCodeData?.searchDate || "",
      searchType: props?.errorCodeData?.searchType || "",
    };
    props.getErrorCodeData(requestData);
  };

  const handleSelectAllClick = event => {
    if (event.target.checked) {
      const newSelecteds = rows.map(n => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    const requestData = {
      Profile_Code: menu?.Role_Permission_Data?.Profile_Code,
      recordPerPage: parseInt(event.target.value, 10),
      pageNo: 1,
      sortColumn: orderBy,
      sortOrder: order,
      searchErrorCode: props?.errorCodeData?.searchErrorCode || "",
      searchDescription: props?.errorCodeData?.searchDescription || "",
      searchDate: props?.errorCodeData?.searchDate || "",
      searchType: props?.errorCodeData?.searchType || "",
    };
    props.getErrorCodeData(requestData);
  };

  const displayAndDeleteChips = searchData => {
    const chips = [];
    if (searchData.searchErrorCode) {
      chips.push(`Error Code: ${searchData.searchErrorCode}`);
    }
    if (searchData.searchDescription) {
      chips.push(`Description: ${searchData.searchDescription}`);
    }
    if (searchData.searchDate) {
      chips.push(`Date: ${searchData.searchDate}`);
    }
    if (searchData.searchType) {
      chips.push(`Type: ${searchData.searchType}`);
    }
    setChipValue(chips);
  };

  const handleDeleteChip = chipToDelete => {
    const updatedChips = chipValue.filter(chip => chip !== chipToDelete);
    setChipValue(updatedChips);
    const profileCode = menu?.Role_Permission_Data?.Profile_Code;
    let payload;
    const updatedFormData = {
      sortColumn: orderBy,
      sortOrder: order,
      recordPerPage: rowsPerPage,
      pageNo: page,
      searchErrorCode: "",
      searchDescription: "",
      searchDate: "",
      searchType: "",
    };

    if (updatedChips.length > 0) {
      updatedChips.forEach(chip => {
        const [key, value] = chip.split(":").map(item => item.trim());
        switch (key) {
        case "Error Code":
          updatedFormData.searchErrorCode = value;
          break;
        case "Description":
          updatedFormData.searchDescription = value;
          break;
        case "Date":
          updatedFormData.searchDate = value;
          break;
        case "Type":
          updatedFormData.searchType = value;
          break;
        default:
          break;
        }
      });
    }
    props.clearFormData();
    props.setFormData(updatedFormData);

    if (profileCode === profileCodePermissionCheck.profileCode.SA) {
      payload = {
        Profile_id: profileId,
        recordPerPage: updatedChips.length >= 0 ? rowsPerPage : 5,
        pageNo: page,
        sortColumn: orderBy,
        sortOrder: order,
        ...updatedFormData,
      };
    } else {
      payload = {
        Profile_Code: profileCode,
        recordPerPage: updatedChips.length >= 0 ? rowsPerPage : 5,
        pageNo: page,
        sortColumn: orderBy,
        sortOrder: order,
        ...updatedFormData,
      };
    }

    props.getErrorCodeData(payload);

    props.clearFormData();
  };

  rows = [];
  const result = props.errorCodeData.errorCodeData?.data?.map(items => ({
    ErrorCode: items?.ErrorCode,
    type: items?.type,
    Description: items?.Description,
    Entered_On: items?.Entered_On,
  }));

  if (result) {
   result.forEach(v => {
      const ErrorCode = v?.ErrorCode;
      const type = v?.type;
      const Description = v?.Description;
      const Entered_On = moment(v?.Entered_On.$date)
        .tz("America/New_York")
        .format("MM/DD/YYYY hh:mm A");
      rows.push(createData(ErrorCode, type, Description, Entered_On));
    });
}

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setOrder(newOrder);
    setOrderBy(property);
    const requestData = {
      Profile_Code: menu?.Role_Permission_Data?.Profile_Code,
      recordPerPage: rowsPerPage,
      pageNo: page,
      sortColumn: property,
      sortOrder: newOrder,
      searchErrorCode: props?.errorCodeData?.searchErrorCode || "",
      searchDescription: props?.errorCodeData?.searchDescription || "",
      searchDate: props?.errorCodeData?.searchDate || "",
      searchType: props?.errorCodeData?.searchType || "",
    };
    props.getErrorCodeData(requestData);
  };

  const handleSearchOpen = () => {
    setSearchBar(true);
  };

  const handleSearchClose = () => {
    setSearchBar(false);
  };

  const exportedBy = props.mainData?.users?.userDetailsById
    && props.mainData?.users?.userDetailsById?.Users_Data[0]?.Email;
  const exportedByFname = props.mainData.users.userDetailsById
    && props.mainData.users.userDetailsById?.Users_Data[0].FName;
  const exportedByLname = props.mainData.users.userDetailsById
    && props.mainData.users.userDetailsById?.Users_Data[0].LName;

  const exportListData = () => {
    const filterData = {
      Profile_Code: menu?.Role_Permission_Data?.Profile_Code,
      recordPerPage: 9999999,
      sortColumn: orderBy,
      sortOrder: order,
      searchErrorCode: props?.errorCodeData?.searchErrorCode || "",
      searchDescription: props?.errorCodeData?.searchDescription || "",
      searchDate: props?.errorCodeData?.searchDate || "",
      searchType: props?.errorCodeData?.searchType || "",
      pageNo: 1,
    };
    filterData.recordPerPage = 99999999;
    filterData.pageNo = 1;
    props.getErrorCodeExport(filterData, data => {
      if (data) {
        const mappedLogs = data.data.map(row => ({
          "Error Code": row.ErrorCode,
          Type: row.type,
          Description: row.Description,
          "Update On": moment(row.Entered_On?.$date)
            .tz("America/New_York")
            .format("MM/DD/YYYY hh:mm A"),
        }));
        exportToCSVProvider(
          mappedLogs,
          "Error Code",
          chipValue,
          exportedBy,
          exportedByFname,
          exportedByLname
        );
      }
    });
  };

  const exportToPDFList = () => {
    const filterData = {
      Profile_Code: menu?.Role_Permission_Data?.Profile_Code,
      recordPerPage: 9999999,
      sortColumn: orderBy,
      sortOrder: order,
      searchErrorCode: props?.errorCodeData?.searchErrorCode || "",
      searchDescription: props?.errorCodeData?.searchDescription || "",
      searchDate: props?.errorCodeData?.searchDate || "",
      searchType: props?.errorCodeData?.searchType || "",
      pageNo: 1,
    };
    props.getErrorCodeExport(filterData, data => {
      const totalCount = data.data.length;
      if (data) {
        const mappedLogs = data.data.map(row => [
          row.ErrorCode,
          row.type,
          row.Description,
          row?.Entered_On?.$date
            ? moment(row.Entered_On.$date)
              .tz("America/New_York")
              .format("MM/DD/YYYY hh:mm A")
            : "",
        ]);

        const headersName = ["Error Code", "Type", "Description", "Update On"];
        exportToPDF(
          headersName,
          mappedLogs,
          "Error Code",
          chipValue,
          exportedBy,
          exportedByFname,
          exportedByLname,
          totalCount
        );
      }
    });
  };

  return (
    <>
      <TableContainer className={classes.TableContainer} component={Paper}>
        {windowWidth && (
          <Grid container className={classes.searchContainer}>
            <Grid item className={classes.margin}>
              <div className={classes.searchWrapper}>
                <SearchFields
                  searchBar={searchBar}
                  handleSearchClose={handleSearchClose}
                  handleSearchOpen={handleSearchOpen}
                  Fields={() => (
                    <ErrorSearchDialog
                      close={() => handleSearchClose()}
                      seacrhChipData={data => displayAndDeleteChips(data)}
                      clearChips={() => setChipValue([])}
                    />
                  )}
                />
                <IconsTooltip title="Export" placement="top">
                  <div className={classes.appSvgIcons}>
                    <ExportIcon
                      aria-controls="simple-menu"
                      aria-haspopup="true"
                      onClick={handleToggle}
                      ref={anchorRef}
                    />
                  </div>
                </IconsTooltip>
                <Popper
                  open={openImportFileModal}
                  anchorEl={anchorRef.current}
                  role={undefined}
                  placement="bottom-start"
                  transition
                  disablePortal
                  style={{
                    zIndex: "10",
                  }}
                >
                  {({ TransitionProps, placement }) => (
                    <Grow
                      {...TransitionProps}
                      style={{
                        transformOrigin:
                          placement === "bottom-start"
                            ? "left top"
                            : "left bottom",
                      }}
                    >
                      <Paper>
                        <ClickAwayListener onClickAway={handleCloseModel}>
                          <MenuList
                            id="menu-list-grow"
                            onKeyDown={handleListKeyDown}
                          >
                            {actionPermission(
                              permissionCheck.Category.setup,
                              permissionCheck.Page.merchant,
                              permissionCheck.option.exportToExcel
                            ) && (
                              <MenuItem
                                onClick={exportListData}
                                style={{
                                  fontSize: "14px",
                                }}
                              >
                                Export to Excel
                              </MenuItem>
                            )}
                            {actionPermission(
                              permissionCheck.Category.setup,
                              permissionCheck.Page.merchant,
                              permissionCheck.option.exportToPdf
                            ) && (
                              <MenuItem
                                onClick={exportToPDFList}
                                style={{
                                  fontSize: "14px",
                                }}
                              >
                                Export to PDF
                              </MenuItem>
                            )}
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </div>
            </Grid>
          </Grid>
        )}
        {!windowWidth && (
          <Grid container className={classes.searchContainer}>
            <Grid item className={classes.margin} style={{ display: "flex" }}>
              <div className={classes.searchWrapper}>
                <SearchFields
                  searchBar={searchBar}
                  handleSearchClose={handleSearchClose}
                  handleSearchOpen={handleSearchOpen}
                  Fields={() => (
                    <ErrorSearchDialog
                      close={() => handleSearchClose()}
                      seacrhChipData={data => displayAndDeleteChips(data)}
                      clearChips={() => setChipValue([])}
                    />
                  )}
                />
                <Popper
                  open={openImportFileModal}
                  anchorEl={anchorRef.current}
                  role={undefined}
                  placement="bottom-start"
                  transition
                  disablePortal
                  style={{
                    zIndex: "10",
                  }}
                >
                  {({ TransitionProps, placement }) => (
                    <Grow
                      {...TransitionProps}
                      style={{
                        transformOrigin:
                          placement === "bottom-start"
                            ? "left top"
                            : "left bottom",
                      }}
                    >
                      <Paper>
                        <ClickAwayListener onClickAway={handleCloseModel}>
                          <MenuList
                            id="menu-list-grow"
                            onKeyDown={handleListKeyDown}
                          >
                            {actionPermission(
                              permissionCheck.Category.setup,
                              permissionCheck.Page.merchant,
                              permissionCheck.option.exportToExcel
                            ) && (
                              <MenuItem
                                style={{
                                  fontSize: "14px",
                                }}
                              >
                                Export to Excel
                              </MenuItem>
                            )}
                            {actionPermission(
                              permissionCheck.Category.setup,
                              permissionCheck.Page.merchant,
                              permissionCheck.option.exportToPdf
                            ) && (
                              <MenuItem
                                style={{
                                  fontSize: "14px",
                                }}
                              >
                                Export to PDF
                              </MenuItem>
                            )}
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </div>
            </Grid>
          </Grid>
        )}
        <SearchChips
          searchValues={chipValue}
          handleDeleteChip={(chip, index) => handleDeleteChip(chip, index)}
        />
        <Table className={classes.table} aria-label="simple table">
          <EnhancedTableHead
            classes={classes}
            numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={rows.length}
          />
          <TableBody>
            {!isEmpty(rows) ? (
              rows.map((row, index) => (
                <TableRow className={classes.cellHeadSign} key={index}>
                  <TableCell className={classes.cellText}>
                    <div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#794411",
                        }}
                      >
                        {row.ErrorCode}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className={classes.cellText} align="left">
                    {row.type}
                  </TableCell>
                  <TableCell
                    className={classes.cellText}
                    align="left"
                    style={{ whiteSpace: "normal", width: "60vw" }}
                  >
                    {row.Description}
                  </TableCell>
                  <TableCell className={classes.cellText} align="left">
                    {row.Entered_On}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell className="no-data" colSpan={12}>
                  <img
                    src={noDataImage}
                    alt=""
                    className={classes.noDataImage}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {rows.length ? (
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[
                    5,
                    10,
                    25,
                    {
                      label: "All",
                      value: props?.errorCodeData?.errorCodeData?.total,
                    },
                  ]}
                  colSpan={12}
                  count={props.errorCodeData.errorCodeData.total}
                  rowsPerPage={rowsPerPage || 5}
                  page={page > 0 ? page - 1 : 0}
                  SelectProps={{
                    inputProps: { "aria-label": "rows per page" },
                    MenuProps: { classes: { paper: classes.selectDropdown } },
                  }}
                  classes={{ menuItem: classes.menuItem }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          ) : (
            ""
          )}
        </Table>
      </TableContainer>
    </>
  );
};

ErrorCodes.propTypes = {
  tableData: PropTypes.object,
  history: PropTypes.any,
  userDetails: PropTypes.object,
  mainData: PropTypes.object,
  getMerchantListData: PropTypes.func,
  getTransactionListData: PropTypes.func,
  getErrorCodeData: PropTypes.func,
  sendInviteApiCall: PropTypes.func,
  emptyReduxState: PropTypes.func,
  sendInviteManagerApiCall: PropTypes.func,
  getErrorCodeExport: PropTypes.func,
};

const mapStateToProps = app => ({
  tableData: app.provideRegistration,
  userDetails: app.app,
  mainData: app,
  roles: app.roles,
  merchantListData: app.merchantInformation,
  transactionListData: app.merchantInformation,
  errorCodeData: app.merchantInformation,
});
export default connect(mapStateToProps, {
  clearFormData,
  setFormData,
  getErrorCodeExport,
  getErrorCodeData,
  sendInviteApiCall,
  emptyReduxState,
  sendInviteManagerApiCall,
})(ErrorCodes);
